import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Paper, Typography, FormControl, InputLabel, Select, MenuItem,
  Chip, CircularProgress, Divider, IconButton, Badge, Button,
  LinearProgress,
} from '@mui/material';
import FilterListIcon    from '@mui/icons-material/FilterList';
import CloseIcon         from '@mui/icons-material/Close';
import LocationOnIcon    from '@mui/icons-material/LocationOn';
import PeopleIcon        from '@mui/icons-material/People';
import RefreshIcon       from '@mui/icons-material/Refresh';
import CheckCircleIcon   from '@mui/icons-material/CheckCircle';
import HourglassIcon     from '@mui/icons-material/HourglassEmpty';
import CancelIcon        from '@mui/icons-material/Cancel';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { getMapaAfiliados, getRolesDisponibles } from '../services/authApi';
import { getDepartamentos, getMunicipios } from '../services/ecolabApi';

const MAPS_KEY   = process.env.REACT_APP_GOOGLE_MAPS_KEY;
const COL_CENTER = { lat: 4.5709, lng: -74.2973 };

// Flag a nivel de módulo: evita cargar el script de Maps más de una vez
let _mapsScriptPromise = null;
function loadMapsScript(key) {
  if (_mapsScriptPromise) return _mapsScriptPromise;
  _mapsScriptPromise = new Promise((resolve, reject) => {
    if (window.google?.maps) { resolve(); return; }
    // Verificar si el script ya está en el DOM (otro componente lo insertó)
    if (document.querySelector('script[src*="maps.googleapis.com"]')) {
      // Esperar a que cargue si aún no está listo
      const check = setInterval(() => {
        if (window.google?.maps) { clearInterval(check); resolve(); }
      }, 50);
      return;
    }
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${key}`;
    s.async = true; s.defer = true;
    s.onload  = resolve;
    s.onerror = (e) => { _mapsScriptPromise = null; reject(e); };
    document.head.appendChild(s);
  });
  return _mapsScriptPromise;
}

const markerScale = t => Math.min(28, Math.max(12, 8 + Math.log2(t + 1) * 5));
const markerColor = p => {
  if (p.pendientes > 0 && p.pendientes >= p.activos) return '#e65100';
  if (p.activos > 0) return '#1a4d2e';
  return '#78909c';
};

const MAP_STYLES = {
  estandar: [],
  oscuro: [
    { elementType:'geometry', stylers:[{color:'#212121'}] },
    { elementType:'labels.text.fill', stylers:[{color:'#757575'}] },
    { elementType:'labels.text.stroke', stylers:[{color:'#212121'}] },
    { featureType:'water', elementType:'geometry', stylers:[{color:'#000000'}] },
    { featureType:'road',  elementType:'geometry', stylers:[{color:'#38414e'}] },
    { featureType:'poi',   stylers:[{visibility:'off'}] },
    { featureType:'transit', stylers:[{visibility:'off'}] },
    { featureType:'administrative.country', elementType:'geometry.stroke', stylers:[{color:'#9e9e9e'}] },
  ],
  claro: [
    { featureType:'all', elementType:'geometry', stylers:[{color:'#f5f5f5'}] },
    { featureType:'water', elementType:'geometry', stylers:[{color:'#c9c9c9'}] },
    { featureType:'road',  elementType:'geometry', stylers:[{color:'#ffffff'}] },
    { featureType:'poi',   stylers:[{visibility:'off'}] },
    { featureType:'transit', stylers:[{visibility:'off'}] },
    { featureType:'administrative.country', elementType:'geometry.stroke', stylers:[{color:'#c0c0c0'},{weight:1.5}] },
  ],
  minimal: [
    { featureType:'all',   elementType:'labels', stylers:[{visibility:'off'}] },
    { featureType:'administrative.country', elementType:'geometry.stroke', stylers:[{color:'#1a4d2e'},{weight:2}] },
    { featureType:'administrative.province', elementType:'geometry.stroke', stylers:[{color:'#4a7c59'},{weight:0.8}] },
    { featureType:'water', elementType:'geometry', stylers:[{color:'#b3d1e8'}] },
    { featureType:'landscape', elementType:'geometry', stylers:[{color:'#f0f4ee'}] },
    { featureType:'road',  stylers:[{visibility:'off'}] },
    { featureType:'poi',   stylers:[{visibility:'off'}] },
    { featureType:'transit', stylers:[{visibility:'off'}] },
    { featureType:'administrative.locality', elementType:'labels', stylers:[{visibility:'on'},{color:'#333333'}] },
  ],
};

// ── Hook drag ────────────────────────────────────────────────────────────
function useDraggable(init) {
  const [pos, setPos] = useState(init);
  const dragging = useRef(false);
  const offset   = useRef({ x: 0, y: 0 });
  const ref      = useRef(null);

  const onMouseDown = useCallback((e) => {
    if (!ref.current) return;
    dragging.current = true;
    const r = ref.current.getBoundingClientRect();
    offset.current = { x: e.clientX - r.left, y: e.clientY - r.top };
    e.preventDefault();
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current || !ref.current) return;
      const cr = ref.current.parentElement?.getBoundingClientRect();
      const pr = ref.current.getBoundingClientRect();
      if (!cr) return;
      let nx = Math.max(0, Math.min(e.clientX - cr.left - offset.current.x, cr.width  - pr.width));
      let ny = Math.max(0, Math.min(e.clientY - cr.top  - offset.current.y, cr.height - pr.height));
      setPos({ x: nx, y: ny });
    };
    const onUp = () => { dragging.current = false; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, []);

  return { pos, ref, onMouseDown };
}

// ════════════════════════════════════════════════════════════════════════
export default function MapaColombiaAdmin() {
  const mapRef  = useRef(null);
  const mapObj  = useRef(null);
  const markers = useRef([]);
  const infoWin = useRef(null);

  const [mapsReady,    setMapsReady]    = useState(false);
  const [loading,      setLoading]      = useState(true);
  const [datos,        setDatos]        = useState([]);
  const [coordsMap,    setCoordsMap]    = useState({});
  const [showFilter,   setShowFilter]   = useState(true);
  const [deptos,       setDeptos]       = useState([]);
  const [munis,        setMunis]        = useState([]);
  const [roles,        setRoles]        = useState([]);
  const [selDepto,     setSelDepto]     = useState('');
  const [selMuni,      setSelMuni]      = useState('');
  const [selRol,       setSelRol]       = useState('');
  const [tema,         setTema]         = useState('estandar');
  const [popup,        setPopup]        = useState(null);   // popup inferior derecho
  const [drawerOpen,   setDrawerOpen]   = useState(false);  // drawer derecho de resultados
  const [drawerMuni,   setDrawerMuni]   = useState(null);   // municipio seleccionado en drawer

  const drag = useDraggable({ x: 14, y: 14 });

  const hayFiltros = !!(selDepto || selMuni || selRol);

  // ── Init ──────────────────────────────────────────────────────────────
  useEffect(() => {
    getDepartamentos(1045).then(setDeptos).catch(() => {});
    getRolesDisponibles().then(setRoles).catch(() => {});
    if (!MAPS_KEY) return;
    loadMapsScript(MAPS_KEY).then(() => setMapsReady(true)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selDepto) { setMunis([]); setSelMuni(''); return; }
    getMunicipios(selDepto).then(setMunis).catch(() => {});
  }, [selDepto]);

  // Aplicar tema
  useEffect(() => {
    if (!mapObj.current) return;
    const SAT = { satelite:'satellite', hibrido:'hybrid', terreno:'terrain' };
    if (SAT[tema]) {
      mapObj.current.setMapTypeId(SAT[tema]);
      mapObj.current.setOptions({ styles: [] });
    } else {
      mapObj.current.setMapTypeId('roadmap');
      mapObj.current.setOptions({ styles: MAP_STYLES[tema] || [] });
    }
  }, [tema]);

  // ── Cargar datos ──────────────────────────────────────────────────────
  const cargarDatos = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selDepto) params.departamentoId = selDepto;
      if (selMuni)  params.municipioId    = selMuni;
      if (selRol)   params.rol            = selRol;
      const res = await getMapaAfiliados(params);
      setDatos(res.puntos || []);
      const cm = {};
      (res.coordenadas || []).forEach(c => {
        const id  = c.municipioID  ?? c.MunicipioID;
        const lat = c.latitud      ?? c.Latitud;
        const lng = c.longitud     ?? c.Longitud;
        if (id && lat && lng) cm[id] = { lat: parseFloat(lat), lng: parseFloat(lng) };
      });
      setCoordsMap(cm);
    } catch { setDatos([]); }
    finally { setLoading(false); }
  }, [selDepto, selMuni, selRol]);

  useEffect(() => { cargarDatos(); }, [cargarDatos]);

  // Abrir drawer automáticamente cuando hay filtros activos y llegan datos
  useEffect(() => {
    if (hayFiltros && !loading) setDrawerOpen(true);
  }, [hayFiltros, loading]);

  // ── Init mapa ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapsReady || !mapRef.current) return;
    mapObj.current = new window.google.maps.Map(mapRef.current, {
      center: COL_CENTER, zoom: 6,
      mapTypeControl: false, streetViewControl: false, fullscreenControl: true,
      restriction: { latLngBounds:{ north:14, south:-5, west:-83, east:-60 }, strictBounds:false },
    });
    infoWin.current = new window.google.maps.InfoWindow();
  }, [mapsReady]);

  // ── InfoWindow ────────────────────────────────────────────────────────
  const abrirInfoWindow = useCallback((punto, marker) => {
    if (!infoWin.current || !mapObj.current) return;
    const pct = punto.total > 0 ? Math.round(punto.activos / punto.total * 100) : 0;
    const rej = punto.total - punto.activos - punto.pendientes;
    infoWin.current.setContent(`
      <div style="font-family:'Segoe UI',sans-serif;min-width:190px;padding:4px 0">
        <div style="font-size:14px;font-weight:700;color:#1a4d2e">${punto.municipio}</div>
        <div style="font-size:11px;color:#888;margin-bottom:10px">${punto.departamento}</div>
        <div style="background:#e8f5e9;border-radius:6px;padding:8px;margin-bottom:8px;text-align:center">
          <span style="font-size:22px;font-weight:900;color:#1a4d2e">${punto.total}</span>
          <span style="font-size:11px;color:#4a7c59;margin-left:4px">inscripción${punto.total!==1?'es':''}</span>
        </div>
        <table style="width:100%;font-size:11px">
          <tr><td style="color:#43a047;padding:2px 0">● Activos</td><td style="text-align:right;font-weight:700;color:#2e7d32">${punto.activos}</td></tr>
          <tr><td style="color:#e65100;padding:2px 0">⏳ Pendientes</td><td style="text-align:right;font-weight:700;color:#e65100">${punto.pendientes}</td></tr>
          ${rej > 0 ? `<tr><td style="color:#c62828;padding:2px 0">✗ Rechazados</td><td style="text-align:right;font-weight:700;color:#c62828">${rej}</td></tr>` : ''}
        </table>
        <div style="margin-top:8px">
          <div style="height:5px;background:#f0f0f0;border-radius:3px;overflow:hidden">
            <div style="height:100%;width:${pct}%;background:linear-gradient(90deg,#43a047,#1a4d2e);border-radius:3px"></div>
          </div>
          <div style="font-size:10px;color:#888;margin-top:2px;text-align:right">${pct}% activos</div>
        </div>
      </div>
    `);
    infoWin.current.open(mapObj.current, marker);
  }, []);

  // ── Marcadores ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapObj.current) return;
    markers.current.forEach(m => m.setMap(null));
    markers.current = [];
    if (datos.length === 0) { mapObj.current.setCenter(COL_CENTER); mapObj.current.setZoom(6); return; }
    const bounds = new window.google.maps.LatLngBounds();
    let cnt = 0;
    datos.forEach(punto => {
      const coord = punto.municipioId ? coordsMap[punto.municipioId] : null;
      if (!coord) return;
      const scale = markerScale(punto.total);
      const color = markerColor(punto);
      const marker = new window.google.maps.Marker({
        position: coord, map: mapObj.current,
        title: `${punto.municipio} — ${punto.total}`,
        zIndex: punto.total,
        icon: { path: window.google.maps.SymbolPath.CIRCLE, scale, fillColor: color, fillOpacity: 0.9, strokeColor: '#fff', strokeWeight: 2.5 },
        label: { text: punto.total > 999 ? '999+' : String(punto.total), color: '#fff', fontSize: scale > 20 ? '11px' : '9px', fontWeight: '700' },
      });
      marker.addListener('click', () => {
        setPopup(punto);
        setDrawerMuni(punto);
        abrirInfoWindow(punto, marker);
      });
      markers.current.push(marker);
      bounds.extend(coord);
      cnt++;
    });
    if (cnt > 0) {
      mapObj.current.fitBounds(bounds, { top: 60, bottom: 40, left: 300, right: 40 });
      if (cnt === 1) mapObj.current.setZoom(12);
    }
  }, [datos, coordsMap, abrirInfoWindow]);

  // Centrar en municipio seleccionado desde el drawer
  const seleccionarMuni = (punto) => {
    setDrawerMuni(punto);
    setPopup(punto);
    const coord = punto.municipioId ? coordsMap[punto.municipioId] : null;
    if (coord && mapObj.current) {
      mapObj.current.panTo(coord);
      mapObj.current.setZoom(11);
      const marker = markers.current.find(m =>
        Math.abs(m.getPosition().lat() - coord.lat) < 0.001 &&
        Math.abs(m.getPosition().lng() - coord.lng) < 0.001
      );
      if (marker) abrirInfoWindow(punto, marker);
    }
  };

  const limpiarFiltros = () => {
    setSelDepto(''); setSelMuni(''); setSelRol('');
    setPopup(null);
    setDrawerOpen(false);
    setDrawerMuni(null);
    infoWin.current?.close();
  };

  // Totales
  const totalAfiliados  = datos.reduce((s, d) => s + d.total, 0);
  const totalActivos    = datos.reduce((s, d) => s + d.activos, 0);
  const totalPendientes = datos.reduce((s, d) => s + d.pendientes, 0);
  const totalRechazados = totalAfiliados - totalActivos - totalPendientes;
  const deptsConDatos   = [...new Set(datos.map(d => d.departamento).filter(Boolean))].length;
  const pctActivos      = totalAfiliados > 0 ? Math.round(totalActivos / totalAfiliados * 100) : 0;
  const filtrosCount    = [selDepto, selMuni, selRol].filter(Boolean).length;

  return (
    <Box sx={{ position: 'relative', height: 'calc(100vh - 112px)', overflow: 'hidden', background: '#e8f0eb' }}>

      {/* Mapa */}
      {MAPS_KEY
        ? <Box ref={mapRef} sx={{ width: '100%', height: '100%' }} />
        : <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
            <LocationOnIcon sx={{ fontSize: 72, color: '#c8e6c9' }} />
            <Typography sx={{ color: '#888', textAlign: 'center' }}>Configure <b>REACT_APP_GOOGLE_MAPS_KEY</b> en .env</Typography>
          </Box>
      }

      {/* Loading bar */}
      {loading && (
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 20 }}>
          <LinearProgress sx={{ '& .MuiLinearProgress-bar': { background: '#c8a84b' }, background: 'rgba(26,77,46,0.3)' }} />
        </Box>
      )}

      {/* ══ PANEL DE FILTROS — draggable ══ */}
      <Box ref={drag.ref} sx={{ position: 'absolute', top: drag.pos.y, left: drag.pos.x, zIndex: 10, userSelect: 'none' }}>

        {/* Botón para mostrar panel cuando está oculto */}
        {!showFilter && (
          <Paper elevation={4} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <IconButton onClick={() => setShowFilter(true)} size="small"
              sx={{ p: 1.3, background: 'linear-gradient(135deg,#1a4d2e,#2e7d32)', color: '#fff', borderRadius: 0, '&:hover':{ background:'#143d22' } }}>
              <Badge badgeContent={filtrosCount} color="warning"><FilterListIcon /></Badge>
            </IconButton>
          </Paper>
        )}

        {showFilter && (
          <Paper elevation={5} sx={{ width: 278, borderRadius: 2.5, overflow: 'hidden', boxShadow: '0 8px 40px rgba(0,0,0,0.22)' }}>

            {/* Header drag */}
            <Box onMouseDown={drag.onMouseDown}
              sx={{ background: 'linear-gradient(135deg,#1a4d2e,#2e7d32)', px: 1.5, py: 1.2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'grab', '&:active':{ cursor:'grabbing' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <DragIndicatorIcon sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 18 }} />
                <FilterListIcon sx={{ color: '#c8a84b', fontSize: 16 }} />
                <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.82rem' }}>Filtros del mapa</Typography>
                {filtrosCount > 0 && <Chip label={filtrosCount} size="small" sx={{ background: '#c8a84b', color: '#1a1a1a', height: 17, fontSize: '0.62rem', fontWeight: 700 }} />}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton size="small" onClick={cargarDatos} disabled={loading} sx={{ color: 'rgba(255,255,255,0.65)', p: 0.3 }}><RefreshIcon sx={{ fontSize: 15 }} /></IconButton>
                {/* Botón ocultar panel */}
                <IconButton size="small" onClick={() => setShowFilter(false)} sx={{ color: 'rgba(255,255,255,0.65)', p: 0.3 }}><CloseIcon sx={{ fontSize: 15 }} /></IconButton>
              </Box>
            </Box>

            <Box sx={{ p: 1.8 }}>
              <FormControl fullWidth size="small" sx={{ mb: 1.2 }}>
                <InputLabel>Departamento</InputLabel>
                <Select value={selDepto} label="Departamento"
                  onChange={e => { setSelDepto(e.target.value); setSelMuni(''); }}>
                  <MenuItem value=""><em>Todos</em></MenuItem>
                  {deptos.map(d => <MenuItem key={d.departamentoID ?? d.DepartamentoID} value={d.departamentoID ?? d.DepartamentoID}>{d.nombre ?? d.Nombre}</MenuItem>)}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small" sx={{ mb: 1.2 }} disabled={!selDepto}>
                <InputLabel>{selDepto ? 'Municipio' : 'Seleccione departamento'}</InputLabel>
                <Select value={selMuni} label="Municipio" onChange={e => setSelMuni(e.target.value)}>
                  <MenuItem value=""><em>Todos</em></MenuItem>
                  {munis.map(m => <MenuItem key={m.municipioID ?? m.MunicipioID} value={m.municipioID ?? m.MunicipioID}>{m.nombre ?? m.Nombre}</MenuItem>)}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small" sx={{ mb: 1.2 }}>
                <InputLabel>Rol</InputLabel>
                <Select value={selRol} label="Rol" onChange={e => setSelRol(e.target.value)}>
                  <MenuItem value=""><em>Todos los roles</em></MenuItem>
                  {roles.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small" sx={{ mb: filtrosCount ? 1.2 : 0 }}>
                <InputLabel>Tema del mapa</InputLabel>
                <Select value={tema} label="Tema del mapa" onChange={e => setTema(e.target.value)}>
                  <MenuItem value="estandar">Estándar</MenuItem>
                  <MenuItem value="satelite">Satélite</MenuItem>
                  <MenuItem value="hibrido">Híbrido</MenuItem>
                  <MenuItem value="terreno">Terreno</MenuItem>
                  <MenuItem value="oscuro">Oscuro</MenuItem>
                  <MenuItem value="claro">Claro</MenuItem>
                  <MenuItem value="minimal">Minimal</MenuItem>
                </Select>
              </FormControl>

              <Button fullWidth size="small" variant="outlined" color="error"
                onClick={limpiarFiltros} disabled={filtrosCount === 0}
                sx={{ borderRadius: 2, fontSize: '0.74rem', mt: 0.8 }}>
                {filtrosCount > 0 ? `Limpiar ${filtrosCount} filtro${filtrosCount>1?'s':''} y cerrar resultados` : 'Limpiar filtros'}
              </Button>
            </Box>
          </Paper>
        )}
      </Box>

      {/* ══ DRAWER LATERAL DERECHO — resultados del filtro ══ */}
      <Box sx={{
        position: 'absolute', top: 0, right: 0, zIndex: 11,
        width: 300, height: '100%',
        transform: drawerOpen ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
        display: 'flex', flexDirection: 'column',
        background: '#fff',
        boxShadow: '-4px 0 24px rgba(0,0,0,0.18)',
        overflow: 'hidden',
      }}>
        {/* Header drawer */}
        <Box sx={{ background: 'linear-gradient(135deg,#1a4d2e,#2e7d32)', px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <Box>
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.88rem' }}>
              Resultados del filtro
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.7rem' }}>
              {filtrosCount > 0 ? `${filtrosCount} filtro${filtrosCount>1?'s':''} activo${filtrosCount>1?'s':''}` : 'Vista general'}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setDrawerOpen(false)} sx={{ color: 'rgba(255,255,255,0.7)' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* KPIs */}
        <Box sx={{ p: 1.8, flexShrink: 0, borderBottom: '1px solid #e0e0e0' }}>
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={24} /></Box>
          ) : (
            <>
              <Box sx={{ background: 'linear-gradient(135deg,#1a4d2e,#2e7d32)', borderRadius: 2, py: 1.3, mb: 1.3, textAlign: 'center' }}>
                <Typography sx={{ color: '#c8a84b', fontWeight: 900, fontSize: '1.8rem', lineHeight: 1 }}>{totalAfiliados}</Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.7rem' }}>inscripciones en total</Typography>
              </Box>

              <Box sx={{ mb: 1.2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                  <Typography variant="caption" sx={{ color: '#666' }}>Tasa de aprobación</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#2e7d32' }}>{pctActivos}%</Typography>
                </Box>
                <Box sx={{ height: 7, background: '#f0f0f0', borderRadius: 4, overflow: 'hidden' }}>
                  <Box sx={{ height: '100%', width: `${pctActivos}%`, background: 'linear-gradient(90deg,#43a047,#1a4d2e)', borderRadius: 4, transition: 'width 0.6s' }} />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 0.8, mb: 1 }}>
                {[
                  { label:'Activos', val:totalActivos, color:'#2e7d32', bg:'#e8f5e9' },
                  { label:'Pendientes', val:totalPendientes, color:'#e65100', bg:'#fff3e0' },
                  { label:'Rechazados', val:totalRechazados, color:'#c62828', bg:'#ffebee' },
                ].map(({ label, val, color, bg }) => (
                  <Box key={label} sx={{ flex:1, textAlign:'center', background:bg, borderRadius:1.5, py:0.8 }}>
                    <Typography sx={{ fontWeight:800, fontSize:'1rem', color }}>{val}</Typography>
                    <Typography variant="caption" sx={{ color:'#888', fontSize:'0.6rem' }}>{label}</Typography>
                  </Box>
                ))}
              </Box>

              <Box sx={{ display: 'flex', gap: 0.8 }}>
                <Box sx={{ flex:1, textAlign:'center', background:'#f5f5f5', borderRadius:1.5, py:0.7 }}>
                  <Typography sx={{ fontWeight:800, fontSize:'0.95rem', color:'#1565c0' }}>{deptsConDatos}</Typography>
                  <Typography variant="caption" sx={{ color:'#888', fontSize:'0.6rem' }}>Departamentos</Typography>
                </Box>
                <Box sx={{ flex:1, textAlign:'center', background:'#f5f5f5', borderRadius:1.5, py:0.7 }}>
                  <Typography sx={{ fontWeight:800, fontSize:'0.95rem', color:'#1565c0' }}>{datos.length}</Typography>
                  <Typography variant="caption" sx={{ color:'#888', fontSize:'0.6rem' }}>Municipios</Typography>
                </Box>
              </Box>
            </>
          )}
        </Box>

        {/* Lista de municipios — scrolleable */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <Box sx={{ px: 2, py: 0.8, background: '#fafafa', borderBottom: '1px solid #eee' }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.65rem' }}>
              Municipios con inscripciones ({datos.length})
            </Typography>
          </Box>

          {datos.length === 0 && !loading && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <LocationOnIcon sx={{ fontSize: 36, color: '#ddd' }} />
              <Typography variant="caption" sx={{ display: 'block', color: '#bbb', mt: 1 }}>Sin resultados con los filtros actuales</Typography>
            </Box>
          )}

          {[...datos].sort((a, b) => b.total - a.total).map(d => {
            const isSelected = drawerMuni?.municipioId === d.municipioId;
            const pct = d.total > 0 ? Math.round(d.activos / d.total * 100) : 0;
            const rej = d.total - d.activos - d.pendientes;
            return (
              <Box key={`${d.departamentoId}-${d.municipioId}`}
                onClick={() => seleccionarMuni(d)}
                sx={{
                  px: 2, py: 1.1, cursor: 'pointer', borderBottom: '1px solid #f5f5f5',
                  background: isSelected ? '#e8f5e9' : 'white',
                  borderLeft: isSelected ? '3px solid #2e7d32' : '3px solid transparent',
                  '&:hover': { background: '#f5fdf5' }, transition: 'all 0.12s',
                }}>
                {/* Nombre + chips */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.6 }}>
                  <Box sx={{ flexGrow: 1, minWidth: 0, pr: 1 }}>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#1a1a1a', lineHeight: 1.3 }}>{d.municipio}</Typography>
                    <Typography sx={{ fontSize: '0.63rem', color: '#aaa' }}>{d.departamento}</Typography>
                  </Box>
                  <Chip label={d.total} size="small"
                    sx={{ height: 18, fontSize: '0.63rem', fontWeight: 700, background: markerColor(d), color: '#fff', minWidth: 26 }} />
                </Box>
                {/* Barra de progreso */}
                <Box sx={{ display: 'flex', gap: 0.3, mb: 0.5 }}>
                  {d.activos > 0 && <Box sx={{ height: 4, flex: d.activos, background: '#43a047', borderRadius: 2 }} />}
                  {d.pendientes > 0 && <Box sx={{ height: 4, flex: d.pendientes, background: '#e65100', borderRadius: 2 }} />}
                  {rej > 0 && <Box sx={{ height: 4, flex: rej, background: '#c62828', borderRadius: 2 }} />}
                </Box>
                {/* Stats compactas */}
                <Box sx={{ display: 'flex', gap: 0.8 }}>
                  {d.activos > 0    && <Typography variant="caption" sx={{ color:'#2e7d32', fontSize:'0.62rem' }}>✓ {d.activos} activos</Typography>}
                  {d.pendientes > 0 && <Typography variant="caption" sx={{ color:'#e65100', fontSize:'0.62rem' }}>⏳ {d.pendientes} pend.</Typography>}
                  {rej > 0          && <Typography variant="caption" sx={{ color:'#c62828', fontSize:'0.62rem' }}>✗ {rej} rech.</Typography>}
                  <Typography variant="caption" sx={{ color:'#bbb', fontSize:'0.62rem', ml:'auto' }}>{pct}%</Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* ══ POPUP detalle municipio — esquina inferior derecha ══ */}
      {popup && (
        <Box sx={{ position: 'absolute', bottom: 24, right: drawerOpen ? 316 : 20, zIndex: 10, transition: 'right 0.3s' }}>
          <Paper elevation={6} sx={{ width: 240, borderRadius: 2.5, overflow: 'hidden', boxShadow: '0 8px 40px rgba(0,0,0,0.22)' }}>
            <Box sx={{ background: `linear-gradient(135deg,${markerColor(popup)},#1a4d2e)`, px: 2, py: 1.3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.88rem' }}>{popup.municipio}</Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.7rem' }}>{popup.departamento}</Typography>
              </Box>
              <IconButton size="small" onClick={() => { setPopup(null); infoWin.current?.close(); }} sx={{ color: 'rgba(255,255,255,0.7)', p: 0.3 }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ px: 2, py: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1.2 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg,#1a4d2e,#43a047)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <PeopleIcon sx={{ color: '#fff', fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 900, fontSize: '1.5rem', color: '#1a4d2e', lineHeight: 1 }}>{popup.total}</Typography>
                  <Typography variant="caption" sx={{ color: '#888' }}>inscripción{popup.total!==1?'es':''}</Typography>
                </Box>
              </Box>
              {popup.total > 0 && (
                <Box sx={{ mb: 1.2 }}>
                  <Box sx={{ height: 7, background: '#f0f0f0', borderRadius: 4, overflow: 'hidden' }}>
                    <Box sx={{ height:'100%', width:`${Math.round(popup.activos/popup.total*100)}%`, background:'linear-gradient(90deg,#66bb6a,#2e7d32)', borderRadius:4 }} />
                  </Box>
                  <Typography variant="caption" sx={{ color:'#888', float:'right' }}>{Math.round(popup.activos/popup.total*100)}% activos</Typography>
                </Box>
              )}
              {[
                { label:'Activos',    val:popup.activos,                                   color:'#2e7d32', bg:'#e8f5e9' },
                { label:'Pendientes', val:popup.pendientes,                                color:'#e65100', bg:'#fff3e0' },
                { label:'Rechazados', val:popup.total-popup.activos-popup.pendientes,      color:'#c62828', bg:'#ffebee' },
              ].filter(r => r.val > 0).map(({ label, val, color, bg }) => (
                <Box key={label} sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', py:0.6, borderBottom:'1px solid #f8f8f8' }}>
                  <Typography variant="caption" sx={{ color:'#666' }}>{label}</Typography>
                  <Chip label={val} size="small" sx={{ height:21, fontSize:'0.7rem', fontWeight:700, background:bg, color }} />
                </Box>
              ))}
            </Box>
          </Paper>
        </Box>
      )}

      {/* Fallback tabla sin Maps */}
      {!MAPS_KEY && datos.length > 0 && (
        <Box sx={{ position:'absolute', top:60, left:0, right:0, bottom:0, background:'#fafafa', overflow:'auto', p:3 }}>
          <Typography variant="h6" sx={{ mb:2, fontWeight:700, color:'#1a3a1a' }}>Distribución geográfica</Typography>
          {[...datos].sort((a,b)=>b.total-a.total).map(d => (
            <Box key={`${d.departamentoId}-${d.municipioId}`} sx={{ display:'flex', justifyContent:'space-between', py:1.2, borderBottom:'1px solid #eee' }}>
              <Box>
                <Typography sx={{ fontWeight:600, fontSize:'0.85rem' }}>{d.municipio}</Typography>
                <Typography variant="caption" sx={{ color:'#888' }}>{d.departamento}</Typography>
              </Box>
              <Box sx={{ display:'flex', gap:0.8 }}>
                <Chip label={`${d.total} total`} size="small" sx={{ background:'#e8f5e9', color:'#1a4d2e', fontWeight:700 }} />
                {d.activos>0    && <Chip label={`${d.activos} activos`}    size="small" color="success" />}
                {d.pendientes>0 && <Chip label={`${d.pendientes} pend.`}   size="small" color="warning" />}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
